import chalk from "chalk";
import { Just, type Maybe, Nothing } from "purify-ts";
import { find, map, prop } from "ramda";
import { type FC, memo, type ReactElement, useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { match, P } from "ts-pattern";
import {
  Callout,
  CalloutIntent,
  DecimalField,
  Item,
  MenuOptionItem,
  MultiSelectMenu,
  Switch,
  TextField,
  VStack,
} from "widgets";

import { FileSelectionTypeEnum, type InputSampleValueInput, WorkflowParameterTypeEnum } from "@/graphql.ts";
import { GenomeComboBox } from "@/modules/entities/components/GenomeComboBox.tsx";
import { FilesSelect } from "@/modules/files/components/FilesSelect.tsx";
import { SampleSelectionInput } from "@/modules/workflow/components/SampleSelectionInput.tsx";
import { WorkflowRegionField } from "@/modules/workflow/components/WorkflowRegionField.tsx";
import { useGetSampleTypeQuery } from "@/modules/workflow/graphql/workflow.gql.ts";
import { useWorkflowEditorStore } from "@/modules/workflow/stores/workflow-editor-store.ts";
import type {
  ActivityControlledValue,
  AllParametersTypes,
  BooleanParamType,
  FileParamType,
  NumberParamType,
  OrganismParamType,
  RegionParamType,
  SampleParamType,
  StringParamType,
  TrackerActivity,
  TrackerParamType,
  WorkflowActivityParameterFieldProps,
  WorkflowErrorConnector,
  WorkflowErrorParameter,
  WorkflowSampleAttributes,
} from "@/modules/workflow/utils/types";
import { ATTRIBUTE_TYPE, COLUMN_SET, FILE_BROWSE_TYPES } from "@/settings/settings.ts";
import type { GetArrayElementType } from "@/types.js";

interface WorkflowParameterFieldProps<T> {
  parameter: WorkflowActivityParameterFieldProps;
  value: T;
  setValue: <U extends T>(value: Maybe<U>) => void;
  validateSingleParameter: () => void;
  error?: WorkflowErrorParameter | WorkflowErrorConnector;
  extraAction?: ReactElement;
  onInternalErrorChange?: (error: boolean) => void;
  // TODO Too specific?
  sampleTypeId?: number;
  getSelectedGenomesIds: () => number[];
  isPlaybookContext?: boolean;
}

const WorkflowParameterFieldRender = ({
  error,
  setValue,
  validateSingleParameter,
  extraAction,
  ...props
}: WorkflowParameterFieldProps<AllParametersTypes>) => {
  /**
   * For mandatory parameters, we must allow the removal of the value
   * because a P2 workflow can be used in a Playbook and the value will be set there.
   */

  const handleValueChange = useCallback(
    (value: number | null) => {
      setValue<NumberParamType>(
        value == null || Number.isNaN(value) || !Number.isFinite(value) ? Nothing : Just([value]),
      );
    },
    [setValue],
  );

  return match<WorkflowActivityParameterFieldProps, ReactElement>(props.parameter)
    .with(
      {
        __typename: "StringParam",
        multiSelect: P.union(P.optional(false), P.nullish),
        stringConstraints: P.union(P.nullish, { controlledValues: P.nullish }),
      },
      (parameter) => {
        const currentValue = props.value as StringParamType | null;

        return (
          <TextField
            label={parameter.label}
            name={parameter.id}
            value={currentValue?.[0] ?? ""}
            onChange={(value) => {
              setValue<StringParamType>(value === "" ? Nothing : Just([value]));
            }}
            onBlur={validateSingleParameter}
            isRequired={!parameter.optional}
            isDisabled={parameter.disabled}
            description={parameter.description}
            isInvalid={!!error}
            errorMessage={error?.message}
            extraAction={extraAction}
          />
        );
      },
    )
    .with(
      {
        __typename: "StringParam",
        stringConstraints: { controlledValues: P.array() },
      },
      (parameter) => {
        const currentValue = props.value as StringParamType | null;
        const selectedKeys = currentValue ? new Set(currentValue) : undefined;

        return (
          <MultiSelectMenu
            selectionMode={parameter.multiSelect === true ? "multiple" : "single"}
            label={parameter.label}
            isRequired={!parameter.optional}
            description={parameter.description}
            isDisabled={parameter.disabled}
            selectedKeys={selectedKeys}
            items={parameter.stringConstraints.controlledValues}
            onSelectionChange={(keys) => {
              let maybeValue: Maybe<StringParamType> = Nothing;

              if (keys === "all") {
                maybeValue = Just(map(prop("value"), parameter.stringConstraints.controlledValues));
              } else if (keys.size > 0) {
                maybeValue = Just(map(String, [...keys]));
              }

              setValue<StringParamType>(maybeValue);
            }}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                validateSingleParameter();
              }
            }}
            isInvalid={!!error}
            errorMessage={error?.message}
            rendered={getSingleValueRenderedCb(currentValue, parameter.stringConstraints.controlledValues)}
            extraAction={extraAction}
            data-testid={parameter.id}
          >
            {(item) => (
              <Item key={item.value}>
                <MenuOptionItem title={item.label ?? item.value} description={item.description} />
              </Item>
            )}
          </MultiSelectMenu>
        );
      },
    )
    .with(
      {
        __typename: "NumberParam",
        multiSelect: P.union(P.optional(false), P.nullish),
        numberConstraints: P.when((i) => i?.controlledValues == null),
      },
      (parameter) => {
        const currentValue = props.value as NumberParamType | null;

        return (
          <DecimalField
            label={parameter.label}
            value={currentValue == null ? undefined : String(currentValue[0])}
            onValueChange={(value) => {
              if (value == null || value !== currentValue?.[0]) {
                handleValueChange(value);
              }
            }}
            onBlur={validateSingleParameter}
            isRequired={!parameter.optional}
            isDisabled={parameter.disabled}
            description={parameter.description}
            isInvalid={!!error}
            errorMessage={error?.message}
            extraAction={extraAction}
            name={parameter.id}
            onInternalErrorChange={props.onInternalErrorChange}
          />
        );
      },
    )
    .with(
      {
        __typename: "NumberParam",
        numberConstraints: { controlledValues: P.array() },
      },
      (parameter) => {
        const currentValue = props.value as NumberParamType | null;
        const selectedKeys = currentValue ? new Set(map(String, currentValue)) : undefined;

        return (
          <MultiSelectMenu
            selectionMode={parameter.multiSelect === true ? "multiple" : "single"}
            label={parameter.label}
            isRequired={!parameter.optional}
            description={parameter.description}
            isDisabled={parameter.disabled}
            selectedKeys={selectedKeys}
            items={parameter.numberConstraints.controlledValues}
            onSelectionChange={(keys) => {
              let maybeValue: Maybe<NumberParamType> = Nothing;

              if (keys === "all") {
                maybeValue = Just(map(prop("value"), parameter.numberConstraints.controlledValues));
              } else if (keys.size > 0) {
                maybeValue = Just(map(Number, [...keys]));
              }

              setValue<NumberParamType>(maybeValue);
            }}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                validateSingleParameter();
              }
            }}
            isInvalid={!!error}
            errorMessage={error?.message}
            rendered={getSingleValueRenderedCb(currentValue, parameter.numberConstraints.controlledValues)}
            extraAction={extraAction}
            data-testid={parameter.id}
          >
            {(item) => (
              <Item key={String(item.value)}>
                <MenuOptionItem title={item.label ?? item.value} description={item.description} />
              </Item>
            )}
          </MultiSelectMenu>
        );
      },
    )
    .with({ __typename: "BooleanParam" }, (parameter) => {
      const currentValue = props.value as BooleanParamType | null;

      return (
        <Switch
          label={parameter.label}
          isSelected={currentValue ?? false}
          onChange={(value) => {
            setValue<BooleanParamType>(Just(value));
            // need to validate manually as the setValue explicitly does not validate as all other fields do it on blur
            validateSingleParameter();
          }}
          isRequired={!parameter.optional}
          isDisabled={parameter.disabled}
          description={parameter.description}
          isInvalid={!!error}
          errorMessage={error?.message}
          extraAction={extraAction}
          name={parameter.id}
        />
      );
    })
    .with({ __typename: "GenomeParam" }, (parameter) => {
      const currentValue = props.value as OrganismParamType | null;

      return (
        <GenomeComboBox
          selectedKey={currentValue}
          onSelectionChange={(key) => {
            let maybeValue: Maybe<number> = Nothing;
            if (key != undefined) {
              maybeValue = Just(Number(key));
            }

            setValue<OrganismParamType>(maybeValue);
            validateSingleParameter();
          }}
          label={parameter.label}
          isRequired={!parameter.optional}
          isDisabled={parameter.disabled}
          description={parameter.description}
          isInvalid={!!error}
          errorMessage={error?.message}
          extraAction={extraAction}
          data-testid={parameter.id}
        />
      );
    })
    .with({ __typename: "FileParam" }, (parameter) => {
      const currentValue = props.value as FileParamType | null;

      const selectableFileTypes: FILE_BROWSE_TYPES[] = [];

      if (parameter.fileConstraints?.fileSelectionType === FileSelectionTypeEnum.File)
        selectableFileTypes.push(FILE_BROWSE_TYPES.REGULAR_FILE);
      if (parameter.fileConstraints?.fileSelectionType === FileSelectionTypeEnum.Folder)
        selectableFileTypes.push(FILE_BROWSE_TYPES.DIRECTORY);

      return (
        <FilesSelect
          label={parameter.label}
          error={error ? new Error(error.message) : undefined}
          selection={currentValue ?? []}
          onSelect={(files) => {
            let maybeValue: Maybe<FileParamType> | null = Nothing;
            if (files.length > 0) {
              maybeValue = Just(files);
            }

            setValue<FileParamType>(maybeValue);
            validateSingleParameter();
          }}
          description={parameter.description}
          singleSelection={!parameter.multiSelect}
          fileDefinitionTypes={parameter.fileConstraints?.fileTypes ?? undefined}
          selectableFileTypes={selectableFileTypes.length > 0 ? selectableFileTypes : undefined}
          baseFilterHint={
            parameter.fileConstraints?.fileTypes
              ? `Only ${parameter.fileConstraints?.fileTypes.join(", ")} files can be selected.`
              : undefined
          }
          isDisabled={parameter.disabled}
          isRequired={!parameter.optional}
          extraAction={extraAction}
          data-testid={parameter.id}
        />
      );
    })
    .with({ __typename: "SampleParam" }, (parameter) => {
      const currentValue = props.value as InputSampleValueInput | null;

      return (
        <SampleSelectionInput
          {...parameter}
          onSelect={(selection: InputSampleValueInput) => {
            let maybeValue: Maybe<SampleParamType> = Nothing;
            if (selection != undefined) {
              maybeValue = Just(selection);
            }

            setValue<SampleParamType>(maybeValue);
            validateSingleParameter();
          }}
          isDisabled={parameter.disabled || !props.sampleTypeId}
          selection={currentValue}
          error={error ? new Error(error.message) : undefined}
          extraAction={extraAction}
          sampleTypeId={props.sampleTypeId}
          isRequired={!parameter.optional}
          data-testid={parameter.id}
        />
      );
    })
    .with({ __typename: "RegionParam" }, (parameter) => {
      const currentValue = props.value as RegionParamType | null;

      return (
        <WorkflowRegionField
          label={parameter.label}
          isPlaybookContext={props.isPlaybookContext}
          isRequired={!parameter.optional}
          isDisabled={parameter.disabled}
          description={parameter.description}
          isInvalid={!!error}
          errorMessage={error?.message}
          onChange={(contigId: number | null, start: number, stop: number) => {
            let maybeValue: Maybe<RegionParamType> = Nothing;
            if (contigId != undefined) {
              maybeValue = Just({
                contigId,
                range: !Number.isNaN(start) && !Number.isNaN(stop) ? { start, stop } : undefined,
              });
            }
            setValue<RegionParamType>(maybeValue);
          }}
          defaultValue={
            currentValue
              ? { contigId: currentValue.contigId, start: currentValue.range?.start, stop: currentValue.range?.stop }
              : undefined
          }
          extraAction={extraAction}
          // onInternalError
          onBlurWithin={() => {
            validateSingleParameter();
          }}
          getSelectedGenomesIds={props.getSelectedGenomesIds}
          data-testid={parameter.id}
          onInternalErrorChange={props.onInternalErrorChange}
        />
      );
    })
    .with({ __typename: "DateParam" }, (parameter) => {
      if (import.meta.env.DEV) {
        console.log(
          chalk.bgCyanBright(`Parameter ${parameter.__typename} '${parameter.label}' not supported`),
          parameter,
        );
      }

      return (
        <Callout intent={CalloutIntent.Warning} title={parameter.label ?? parameter.__typename}>
          <FormattedMessage id="parameter not supported" values={{ type: parameter.__typename }} />
        </Callout>
      );
    })
    .with({ __typename: "TrackerParam" }, (parameter) => {
      const currentValue = props.value as TrackerParamType | null;

      return (
        <TrackerParam
          currentValue={currentValue}
          validate={validateSingleParameter}
          {...parameter}
          error={error ? new Error(error.message) : undefined}
          setValue={setValue}
        />
      );
    })
    .with(
      {
        __typename: "StringParam",
        multiSelect: true,
        stringConstraints: P.union(P.nullish, { controlledValues: P.nullish }),
      },
      (parameter) => {
        // Can't be multi select and have no controlled values
        if (import.meta.env.DEV) {
          console.log(
            chalk.bgRedBright(`Parameter ${parameter.__typename} '${parameter.label}' is misconfigured`),
            parameter,
          );
        }

        return (
          <Callout intent={CalloutIntent.Danger} title={parameter.label ?? parameter.__typename}>
            <FormattedMessage id="parameter not supported" values={{ type: parameter.__typename }} />
          </Callout>
        );
      },
    )
    .with(
      {
        __typename: "NumberParam",
        multiSelect: P.boolean,
        numberConstraints: P._,
      },
      (parameter) => {
        // Can't be multi select and have no controlled values. Or any other weird configuration. Hard to spot them all properly.
        if (import.meta.env.DEV) {
          console.log(
            chalk.bgRedBright(`Parameter ${parameter.__typename} '${parameter.label}' is misconfigured`),
            parameter,
          );
        }

        return (
          <Callout intent={CalloutIntent.Danger} title={parameter.label ?? parameter.__typename}>
            <FormattedMessage id="parameter not supported" values={{ type: parameter.__typename }} />
          </Callout>
        );
      },
    )
    .exhaustive();
};

const TrackerParam: FC<
  WorkflowActivityParameterFieldProps & {
    currentValue: TrackerParamType | null;
    error: Error | undefined;
    validate: () => void;
    setValue: <U extends AllParametersTypes>(value: Maybe<U>) => void;
  }
> = ({ currentValue, error, validate, setValue, ...parameter }) => {
  const { id } = parameter;
  const selectedKeys = currentValue ? new Set([String(currentValue)]) : undefined;
  const checkTrackerParameters = useWorkflowEditorStore((state) => state.checkTrackerParameters);
  const parameters = useWorkflowEditorStore((state) => state.getParameters());

  const sampleTypeId = useWorkflowEditorStore((state) => state.sampleTypeId);
  const {
    data,
    loading,
    error: getSampleTypeError,
  } = useGetSampleTypeQuery({
    variables: { sampleTypeId: sampleTypeId!, excludeAnalytical: false },
    skip: sampleTypeId == null,
  });

  const items = useMemo(() => {
    const attributeIsAlreadySelected = (attributeId: number): boolean =>
      parameters
        .filter((parameter) => parameter.typeName === "TrackerParam" && parameter.parameterId !== id)
        // .filter(
        //   (parameter) =>
        //     parameter.typeName === "TrackerParam" &&
        //     (parameter.parameterId !== id || (parameter.parameterId === id && parameter.nodeId !== nodeId)),
        // )
        .map((parameter) => parameter.values.extractNullable() as TrackerParamType | null)
        .includes(attributeId);

    return (
      data?.sample?.sampleType?.sampleAttributes.filter(
        (attribute) =>
          isAttributeSelectableForParamMapping(attribute, parameter as TrackerActivity) &&
          !attributeIsAlreadySelected(attribute.id),
      ) ?? []
    );
  }, [parameters, id, parameter, data]);

  return (
    <VStack>
      <MultiSelectMenu
        selectedKeys={selectedKeys}
        isRequired={!parameter.optional}
        label={parameter.label}
        description={parameter.description}
        isDisabled={parameter.disabled || loading || items.length === 0 || !sampleTypeId}
        isLoading={loading}
        placeholder={items.length === 0 && !loading ? "No attributes available" : "Select an attribute"}
        onSelectionChange={(keys) => {
          if (keys instanceof Set) {
            const selection = [...keys][0];
            if (selection == null) {
              setValue(Nothing);
            } else {
              setValue(Just(Number(selection)));
            }
          }
        }}
        items={items}
        errorMessage={getSampleTypeError?.message ?? error?.message}
        isInvalid={!!getSampleTypeError || !!error}
        selectionMode="single"
        rendered={(keys) => {
          if (keys instanceof Set) {
            const selection = [...keys][0];
            if (selection != undefined) {
              const item = find((type) => String(type.id) === selection, items);
              return item?.attributeName;
            }
          }
          return;
        }}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            validate();
            checkTrackerParameters();
          }
        }}
      >
        {(item) => (
          <Item key={item.id} textValue={item.attributeName}>
            <MenuOptionItem title={item.attributeName} description={item.groupName} />
          </Item>
        )}
      </MultiSelectMenu>
      {/* We removed the warning level in context of SEL10-1120 because the tracker params might also be mandatory now,
      meaning that these inputs do not only trigger warnings but also plain errors.
      Since we have no way to distinguish errors from warnings at this stage and it is too close to the release to do so,
      we simply remove the warning level callouts to the benefit of errors only, although this one specific error "data may be overwritten" is descrete and allows execution and publication.
      alreadySelected && (
        <Callout intent={CalloutIntent.Warning}>
          This attribute was selected in a different activity, so the data may be overwritten.
        </Callout>
      ) */}
    </VStack>
  );
};

const getSingleValueRenderedCb = (
  currentValue: (string | number)[] | undefined | null,
  items: ActivityControlledValue[],
) =>
  currentValue && currentValue.length === 1
    ? () => {
        const item = find((item) => String(item.value) === String(currentValue[0]), items);
        return item!.label ?? item!.value;
      }
    : undefined;

const isAttributeSelectableForParamMapping = (
  attribute: GetArrayElementType<WorkflowSampleAttributes>,
  parameter: TrackerActivity,
) => {
  const editableAttribute =
    // we allow now both analytical and annotation attributes as save targets (SEL10-1395);
    // this is in sync with the fact that both can be configured as inputs (although with some limited validation)
    //
    // we allow non-modifiable attributes since this is some kind of automation, not directly related to user modification (and consistent with the P1 mappings)
    // automatic attributes are excluded because they are not valid input for registration by the REST API via playbooks
    !attribute.automatic &&
    (attribute.columnSet.id === COLUMN_SET.COS_ANALYTICAL_ID.id ||
      attribute.columnSet.id === COLUMN_SET.COS_ANNOTATION_ID.id);

  // make sure param and attribute types are compatible
  const attributeTypeId = attribute.attributeType.id as ATTRIBUTE_TYPE;
  const matchingType = match<TrackerActivity, boolean>(parameter)
    .with(
      { type: WorkflowParameterTypeEnum.Text },
      () => attributeTypeId === ATTRIBUTE_TYPE.FREE_TEXT || attributeTypeId === ATTRIBUTE_TYPE.CONTROLLED_TEXT,
    )
    .with({ type: WorkflowParameterTypeEnum.Number }, () => attributeTypeId === ATTRIBUTE_TYPE.FREE_NUMERICAL)
    .with({ type: WorkflowParameterTypeEnum.Boolean }, () => attributeTypeId === ATTRIBUTE_TYPE.BOOLEAN)
    .with({ type: WorkflowParameterTypeEnum.Date }, () => attributeTypeId === ATTRIBUTE_TYPE.DATE)
    .with({ type: WorkflowParameterTypeEnum.File }, () => attributeTypeId === ATTRIBUTE_TYPE.FILE)
    .otherwise(() => {
      // Do nothing here. The components are in the Tracker Tab.
      if (import.meta.env.DEV) {
        console.log(
          chalk.bgCyanBright(
            `Parameter ${parameter.__typename} '${parameter.label}' of type '${parameter.type}' should not be rendered with Tracker attribute mappings.`,
          ),
          parameter,
        );
      }
      return false;
    });

  // multi params should only go into multi attributes, but single values might as well go into multi value attributes
  const matchingMultiValues = !parameter.multiSelect || parameter.multiSelect === attribute.multipleValues;

  return editableAttribute && matchingType && matchingMultiValues;
};

export const WorkflowParameterField = memo(WorkflowParameterFieldRender);
